import { DEFAULT_BRAIN } from '@/components/constants';
import {
  type components,
  ContentType,
  DictationCleanupModes,
  GenerationMethod,
  HeidiPlatforms,
  VoiceStyle,
} from '@heidi/schema/ml-scribe';

import { type OptimisticPatch } from '@heidi/session/data';
import { type ConfigInput } from './config-input';

// Only values already read at the live request-build point. In particular,
// personalisation is resolved by the body, not re-evaluated in the shadow.
export type ConfigEnv = {
  contextContent: string | undefined;
  cachedDictationCleanupMode: DictationCleanupModes | null | undefined;
  defaultDictationCleanupMode: DictationCleanupModes | null | undefined;
  shouldApplyPersonalizationInsights: boolean | undefined;
  enableBlockEditorV1: boolean;
  isTauri: boolean;
  isMobile: boolean;
  transcriptionV2: boolean;
  demoMode: 'shortened' | undefined;
};

export type ConfigResolution = {
  request: components['schemas']['GenerateConsultNoteRequest'];
  optimisticPatch: OptimisticPatch;
};

// Pure and operator-for-operator equivalent to the live inline request.
// Existing client defaults are reproduced for observation, not changed here.
export function resolveConfig(input: ConfigInput, env: ConfigEnv): ConfigResolution {
  return {
    request: {
      generation_method: input.generationMethod ?? GenerationMethod.TEMPLATE,
      addition: env.contextContent ? env.contextContent : '',
      ai_command_text: input.aiCommandText,
      ai_command_id: input.aiCommandId,
      template_id: input.templateId,
      voice_style: input.voiceStyle || VoiceStyle.GOLDILOCKS,
      custom_style: input.customStyle ?? undefined,
      brain: input.brain || DEFAULT_BRAIN,
      dictation_cleanup_mode:
        input.dictationCleanupMode ||
        env.cachedDictationCleanupMode ||
        env.defaultDictationCleanupMode ||
        DictationCleanupModes.SMART,
      content_type:
        input.contentType ||
        (env.enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN),
      platform: env.isTauri
        ? HeidiPlatforms.Desktop
        : env.isMobile
          ? HeidiPlatforms.MobileWeb
          : HeidiPlatforms.Web,
      is_personalization_applied: env.shouldApplyPersonalizationInsights,
      ignore_backup: env.transcriptionV2,
      demo_mode: env.demoMode,
    },
    optimisticPatch: buildOptimisticPatch(input),
  };
}

// Only truthy caller attributes. Merging these preserves the live `input || old`
// semantics, including empty command text. feError remains owned by the body.
export function buildOptimisticPatch(input: ConfigInput): OptimisticPatch {
  const patch: OptimisticPatch = {};
  if (input.voiceStyle) patch.voiceStyle = input.voiceStyle;
  if (input.brain) patch.brain = input.brain;
  if (input.templateId) patch.templateId = input.templateId;
  if (input.aiCommandText) patch.aiCommandText = input.aiCommandText;
  if (input.generationMethod) patch.generationMethod = input.generationMethod;
  return patch;
}
